"use client";

import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import {
  BOARD_HEIGHT,
  BOARD_WIDTH,
  LegacyGame,
  PIECE_COLORS,
  canPlace,
  cloneBoard,
  createEmptyBoard,
  getPoseCells,
  gravityIntervalAtElapsedTick,
  gravityLevelAtElapsedTick,
  type ActivePiece,
  type Board,
  type Cell,
  type GameEvent,
  type InputControl,
  type PieceId,
} from "../../lib/game";
import { applyInputToActiveGame, quantizeInputTransitionTick } from "../battle-boundaries";
import { loadKeyBindings, useLegacyKeyboard, type KeyBindings } from "../hooks/useLegacyKeyboard";
import { TouchControls, type LegacyAction } from "./TouchControls";

type PaletteItem = {
  value: BoardTool;
  label: string;
  description: string;
  shortcut: string;
};

type BoardTool = Cell | "garbage-line" | "garbage-cell";

type QueuedEditorInput = {
  applyBeforeTick: number;
  control: InputControl;
  held: boolean;
  sequence: number;
};

type BoardSnapshot = {
  id: string;
  board: Board;
  active: ActivePiece | null;
};

type ZipFile = {
  name: string;
  data: Uint8Array;
};

const palette: readonly PaletteItem[] = [
  { value: "garbage-line", label: "お邪魔1列", description: "お邪魔ブロックを1列生成する", shortcut: "0" },
  { value: 1, label: "I", description: "Iミノを上から生成する", shortcut: "1" },
  { value: 2, label: "S", description: "Sミノを上から生成する", shortcut: "2" },
  { value: 3, label: "Z", description: "Zミノを上から生成する", shortcut: "3" },
  { value: 4, label: "O", description: "Oミノを上から生成する", shortcut: "4" },
  { value: 5, label: "T", description: "Tミノを上から生成する", shortcut: "5" },
  { value: 6, label: "L", description: "Lミノを上から生成する", shortcut: "6" },
  { value: 7, label: "J", description: "Jミノを上から生成する", shortcut: "7" },
  { value: 0, label: "消去", description: "選択したマスを空にする", shortcut: "8" },
  { value: "garbage-cell", label: "お邪魔1個", description: "盤面をクリックした場所にお邪魔ブロックを1個置く", shortcut: "9" },
];

const digitTools: Readonly<Record<string, BoardTool>> = {
  "0": "garbage-line",
  "1": 1,
  "2": 2,
  "3": 3,
  "4": 4,
  "5": 5,
  "6": 6,
  "7": 7,
  "8": 0,
  "9": "garbage-cell",
};

const ACTION_TO_INPUT: Readonly<Record<LegacyAction, InputControl>> = {
  ccw: "rotateCCW",
  cw: "rotateCW",
  left: "left",
  right: "right",
  softDrop: "softDrop",
  hardDrop: "hardDrop",
};

const EMPTY_REQUESTED_INPUTS: Record<InputControl, boolean> = {
  left: false,
  right: false,
  rotateCCW: false,
  rotateCW: false,
  softDrop: false,
  hardDrop: false,
};

const PIECE_LABELS: Readonly<Record<PieceId, string>> = {
  1: "I",
  2: "S",
  3: "Z",
  4: "O",
  5: "T",
  6: "L",
  7: "J",
};

const PLAYBACK_INTERVAL_MS = 500;
const PLAYBACK_SKIP_FRAMES = 5;
const MAX_SNAPSHOT_FRAMES = 6;

const cellStyle = (value: Cell): CSSProperties => ({ "--piece": value } as CSSProperties);

function isPieceId(value: Cell): value is PieceId {
  return value >= 1 && value <= 7;
}

function newEditorSeed(): string {
  if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
  return Array.from(
    crypto.getRandomValues(new Uint8Array(16)),
    (byte) => byte.toString(16).padStart(2, "0"),
  ).join("");
}

function activeSnapshotKey(blockIndex: number, active: ActivePiece): string {
  return `${blockIndex}:${active.piece}:${active.rotation}:${active.x}:${active.y}`;
}

function appendRecentSnapshot(current: readonly BoardSnapshot[], snapshot: BoardSnapshot): BoardSnapshot[] {
  return [...current, snapshot].slice(-MAX_SNAPSHOT_FRAMES);
}

function resetRequestedInputs(): Record<InputControl, boolean> {
  return { ...EMPTY_REQUESTED_INPUTS };
}

function addRandomGarbageRow(source: Board): { board: Board; holeColumn: number } {
  const board = cloneBoard(source);
  const holeColumn = Math.floor(Math.random() * BOARD_WIDTH);
  board.shift();
  const row = Array<Cell>(BOARD_WIDTH).fill(8);
  row[holeColumn] = 0;
  board.push(row);
  return { board, holeColumn };
}

function createBoardCanvas(board: Board, active: ActivePiece | null): HTMLCanvasElement {
  const scale = 32;
  const canvas = document.createElement("canvas");
  canvas.width = BOARD_WIDTH * scale;
  canvas.height = BOARD_HEIGHT * scale;
  const context = canvas.getContext("2d");
  if (!context) return canvas;

  context.fillStyle = "#02070d";
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.strokeStyle = "#31506b";
  context.lineWidth = 1;

  const activeCells = new Map(
    active
      ? getPoseCells(active)
        .filter(({ x, y }) => x >= 1 && x <= BOARD_WIDTH && y >= 1 && y <= BOARD_HEIGHT)
        .map(({ x, y }) => [`${x}:${y}`, active.piece] as const)
      : [],
  );

  for (let row = 0; row < BOARD_HEIGHT; row += 1) {
    for (let column = 0; column < BOARD_WIDTH; column += 1) {
      const left = column * scale;
      const top = row * scale;
      context.strokeRect(left + 0.5, top + 0.5, scale - 1, scale - 1);
      const value = activeCells.get(`${column + 1}:${row + 1}`) ?? board[row][column];
      if (value === 0) continue;
      context.fillStyle = PIECE_COLORS[value];
      context.fillRect(left + 2, top + 2, scale - 4, scale - 4);
      context.strokeStyle = "#ffffff99";
      context.strokeRect(left + 2.5, top + 2.5, scale - 5, scale - 5);
      context.strokeStyle = "#31506b";
    }
  }

  return canvas;
}

function boardPngBytes(board: Board, active: ActivePiece | null): Promise<Uint8Array> {
  return new Promise((resolve) => {
    createBoardCanvas(board, active).toBlob((blob) => {
      if (!blob) {
        resolve(new Uint8Array());
        return;
      }
      void blob.arrayBuffer().then((buffer) => resolve(new Uint8Array(buffer)));
    }, "image/png");
  });
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.download = filename;
  link.href = url;
  link.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 1_000);
}

function pushUint16(target: number[], value: number): void {
  target.push(value & 0xff, (value >>> 8) & 0xff);
}

function pushUint32(target: number[], value: number): void {
  target.push(value & 0xff, (value >>> 8) & 0xff, (value >>> 16) & 0xff, (value >>> 24) & 0xff);
}

function crc32(bytes: Uint8Array): number {
  let crc = 0xffffffff;
  for (const byte of bytes) {
    crc ^= byte;
    for (let bit = 0; bit < 8; bit += 1) {
      crc = (crc >>> 1) ^ ((crc & 1) ? 0xedb88320 : 0);
    }
  }
  return (crc ^ 0xffffffff) >>> 0;
}

function createStoredZip(files: readonly ZipFile[]): Blob {
  const output: number[] = [];
  const centralDirectory: number[] = [];
  let offset = 0;

  for (const file of files) {
    const name = new TextEncoder().encode(file.name);
    const checksum = crc32(file.data);
    const localHeader: number[] = [];
    pushUint32(localHeader, 0x04034b50);
    pushUint16(localHeader, 20);
    pushUint16(localHeader, 0);
    pushUint16(localHeader, 0);
    pushUint16(localHeader, 0);
    pushUint16(localHeader, 0);
    pushUint32(localHeader, checksum);
    pushUint32(localHeader, file.data.length);
    pushUint32(localHeader, file.data.length);
    pushUint16(localHeader, name.length);
    pushUint16(localHeader, 0);
    output.push(...localHeader, ...name, ...file.data);

    const centralHeader: number[] = [];
    pushUint32(centralHeader, 0x02014b50);
    pushUint16(centralHeader, 20);
    pushUint16(centralHeader, 20);
    pushUint16(centralHeader, 0);
    pushUint16(centralHeader, 0);
    pushUint16(centralHeader, 0);
    pushUint16(centralHeader, 0);
    pushUint32(centralHeader, checksum);
    pushUint32(centralHeader, file.data.length);
    pushUint32(centralHeader, file.data.length);
    pushUint16(centralHeader, name.length);
    pushUint16(centralHeader, 0);
    pushUint16(centralHeader, 0);
    pushUint16(centralHeader, 0);
    pushUint16(centralHeader, 0);
    pushUint32(centralHeader, 0);
    pushUint32(centralHeader, offset);
    centralDirectory.push(...centralHeader, ...name);
    offset += localHeader.length + name.length + file.data.length;
  }

  const centralOffset = output.length;
  output.push(...centralDirectory);
  const endOfCentralDirectory: number[] = [];
  pushUint32(endOfCentralDirectory, 0x06054b50);
  pushUint16(endOfCentralDirectory, 0);
  pushUint16(endOfCentralDirectory, 0);
  pushUint16(endOfCentralDirectory, files.length);
  pushUint16(endOfCentralDirectory, files.length);
  pushUint32(endOfCentralDirectory, centralDirectory.length);
  pushUint32(endOfCentralDirectory, centralOffset);
  pushUint16(endOfCentralDirectory, 0);
  output.push(...endOfCentralDirectory);
  return new Blob([Uint8Array.from(output)], { type: "application/zip" });
}

const GIF_PALETTE = [
  "#02070d",
  "#31506b",
  PIECE_COLORS[1],
  PIECE_COLORS[2],
  PIECE_COLORS[3],
  PIECE_COLORS[4],
  PIECE_COLORS[5],
  PIECE_COLORS[6],
  PIECE_COLORS[7],
  PIECE_COLORS[8],
  "#f4f7fb",
  "#4f555a",
  "#7c8791",
  "#aab5bf",
  "#d8e6f4",
  "#ffffff",
] as const;

function hexColorBytes(hex: string): readonly [number, number, number] {
  return [
    Number.parseInt(hex.slice(1, 3), 16),
    Number.parseInt(hex.slice(3, 5), 16),
    Number.parseInt(hex.slice(5, 7), 16),
  ];
}

function gifPaletteIndex(value: Cell): number {
  if (value === 0) return 0;
  if (value === 8) return 9;
  return value + 1;
}

function snapshotGifPixels(snapshot: BoardSnapshot): Uint8Array {
  const scale = 32;
  const width = BOARD_WIDTH * scale;
  const height = BOARD_HEIGHT * scale;
  const pixels = new Uint8Array(width * height);
  const activeCells = new Map(
    snapshot.active
      ? getPoseCells(snapshot.active)
        .filter(({ x, y }) => x >= 1 && x <= BOARD_WIDTH && y >= 1 && y <= BOARD_HEIGHT)
        .map(({ x, y }) => [`${x}:${y}`, snapshot.active?.piece ?? 0] as const)
      : [],
  );

  for (let row = 0; row < BOARD_HEIGHT; row += 1) {
    for (let column = 0; column < BOARD_WIDTH; column += 1) {
      const fixed = snapshot.board[row][column];
      const active = activeCells.get(`${column + 1}:${row + 1}`);
      const value = active ?? fixed;
      const colorIndex = gifPaletteIndex(value);
      for (let y = 0; y < scale; y += 1) {
        for (let x = 0; x < scale; x += 1) {
          const isGrid = x === 0 || y === 0;
          pixels[(row * scale + y) * width + column * scale + x] = isGrid ? 1 : colorIndex;
        }
      }
    }
  }
  return pixels;
}

function gifLzwData(pixels: Uint8Array): Uint8Array {
  const minimumCodeSize = 4;
  const clearCode = 1 << minimumCodeSize;
  const endCode = clearCode + 1;
  const bytes: number[] = [];
  let bitBuffer = 0;
  let bitCount = 0;
  // Keep the stream at the initial five-bit code size.  A decoder adds
  // dictionary entries while reading literal pixels, so periodically
  // clearing the dictionary avoids the easy-to-miss encoder/decoder timing
  // difference at a code-size boundary.  This is intentionally a little
  // larger than a compressed stream, but remains small enough for snapshots
  // and is much more portable across GIF decoders.
  const codeSize = minimumCodeSize + 1;

  const writeCode = (code: number) => {
    bitBuffer |= code << bitCount;
    bitCount += codeSize;
    while (bitCount >= 8) {
      bytes.push(bitBuffer & 0xff);
      bitBuffer >>>= 8;
      bitCount -= 8;
    }
  };

  writeCode(clearCode);

  let literalCount = 0;
  for (let index = 0; index < pixels.length; index += 1) {
    writeCode(pixels[index]);
    literalCount += 1;
    // Eight literals create only seven dictionary entries after a clear,
    // safely below the first five-to-six-bit code-size boundary.
    if (literalCount === 8 && index < pixels.length - 1) {
      writeCode(clearCode);
      literalCount = 0;
    }
  }

  writeCode(endCode);
  if (bitCount > 0) bytes.push(bitBuffer & 0xff);
  return Uint8Array.from(bytes);
}

function createAnimatedGif(snapshots: readonly BoardSnapshot[]): Blob {
  const width = BOARD_WIDTH * 32;
  const height = BOARD_HEIGHT * 32;
  const bytes: number[] = [];
  for (const character of "GIF89a") bytes.push(character.charCodeAt(0));
  pushUint16(bytes, width);
  pushUint16(bytes, height);
  bytes.push(0xf3, 0, 0);
  for (const color of GIF_PALETTE) bytes.push(...hexColorBytes(color));

  bytes.push(0x21, 0xff, 0x0b);
  for (const character of "NETSCAPE2.0") bytes.push(character.charCodeAt(0));
  bytes.push(0x03, 0x01, 0x00, 0x00, 0x00);

  for (const snapshot of snapshots) {
    const lzw = gifLzwData(snapshotGifPixels(snapshot));
    bytes.push(0x21, 0xf9, 0x04, 0x04, 50, 0, 0, 0);
    bytes.push(0x2c);
    pushUint16(bytes, 0);
    pushUint16(bytes, 0);
    pushUint16(bytes, width);
    pushUint16(bytes, height);
    bytes.push(0);
    bytes.push(4);
    for (let offset = 0; offset < lzw.length; offset += 255) {
      const length = Math.min(255, lzw.length - offset);
      bytes.push(length, ...lzw.subarray(offset, offset + length));
    }
    bytes.push(0);
  }
  bytes.push(0x3b);
  return new Blob([Uint8Array.from(bytes)], { type: "image/gif" });
}

export function BoardEditorPage() {
  const [board, setBoard] = useState<Board>(() => createEmptyBoard());
  const [selectedTool, setSelectedTool] = useState<BoardTool>("garbage-line");
  const [painting, setPainting] = useState(false);
  const [message, setMessage] = useState("テトリミノを押すと上から生成。ポーズが変わるたび自動保存し、着地後の盤面も最後に追加します。");
  const [activePiece, setActivePiece] = useState<ActivePiece | null>(null);
  const [running, setRunning] = useState(false);
  const [paused, setPaused] = useState(false);
  const [keyBindings] = useState<KeyBindings>(() => loadKeyBindings());
  const [snapshots, setSnapshots] = useState<BoardSnapshot[]>([]);
  const [draggingSnapshotId, setDraggingSnapshotId] = useState<string | null>(null);
  const [exportingSnapshots, setExportingSnapshots] = useState(false);
  const [playbackFrame, setPlaybackFrame] = useState(0);
  const [playbackPlaying, setPlaybackPlaying] = useState(false);

  const boardRef = useRef(board);
  boardRef.current = board;
  const gameRef = useRef<LegacyGame | undefined>(undefined);
  const runningRef = useRef(false);
  const originTimeRef = useRef(0);
  const inputSequenceRef = useRef(0);
  const inputOrderRef = useRef(0);
  const queuedInputsRef = useRef<QueuedEditorInput[]>([]);
  const requestedInputsRef = useRef<Record<InputControl, boolean>>(resetRequestedInputs());
  const snapshotSequenceRef = useRef(0);
  const automaticSnapshotKeyRef = useRef<string | null>(null);

  const activeMap = useMemo(() => {
    if (!activePiece) return new Map<string, { x: number; y: number }>();
    return new Map(getPoseCells(activePiece).map(({ x, y }) => [`${x}:${y}`, { x, y }]));
  }, [activePiece]);

  const captureAutomaticSnapshot = useCallback((sourceBoard: Board, sourceActive: ActivePiece, blockIndex: number) => {
    const key = activeSnapshotKey(blockIndex, sourceActive);
    if (automaticSnapshotKeyRef.current === key) return;
    automaticSnapshotKeyRef.current = key;
    const snapshot: BoardSnapshot = {
      id: `snapshot-${Date.now()}-${++snapshotSequenceRef.current}`,
      board: cloneBoard(sourceBoard),
      active: { ...sourceActive },
    };
    setSnapshots((current) => appendRecentSnapshot(current, snapshot));
  }, []);

  const captureSettledSnapshot = useCallback((sourceBoard: Board) => {
    const snapshot: BoardSnapshot = {
      id: `snapshot-${Date.now()}-${++snapshotSequenceRef.current}`,
      board: cloneBoard(sourceBoard),
      active: null,
    };
    setSnapshots((current) => appendRecentSnapshot(current, snapshot));
  }, []);

  const clearActiveGame = useCallback(() => {
    runningRef.current = false;
    gameRef.current = undefined;
    queuedInputsRef.current = [];
    requestedInputsRef.current = resetRequestedInputs();
    automaticSnapshotKeyRef.current = null;
    setActivePiece(null);
    setRunning(false);
    setPaused(false);
  }, []);

  const processEvents = useCallback((events: readonly GameEvent[]) => {
    for (const event of events) {
      if (event.type === "lock") {
        captureSettledSnapshot(event.board);
        setBoard(event.board);
        clearActiveGame();
        setMessage(
          event.clearedLines > 0
            ? `${event.clearedLines}ライン消去して着地しました。次のミノを選べます。`
            : "着地しました。次のミノを選べます。",
        );
        return;
      }
      if (event.type === "topout") {
        clearActiveGame();
        setMessage("出現位置が埋まっているため、生成できません。盤面を調整してください。");
        return;
      }
    }
  }, [captureSettledSnapshot, clearActiveGame]);

  const advanceGame = useCallback((targetTick: number) => {
    const game = gameRef.current;
    if (!game || !runningRef.current || targetTick < game.tick) return;

    while (
      runningRef.current
      && gameRef.current === game
      && queuedInputsRef.current[0]?.applyBeforeTick <= targetTick
      && !game.topOut
    ) {
      const queued = queuedInputsRef.current.shift();
      if (!queued) break;
      const tickBeforeSample = Math.max(game.tick, queued.applyBeforeTick - 1);
      if (tickBeforeSample > game.tick) {
        processEvents(game.stepTo(tickBeforeSample));
        if (!runningRef.current || gameRef.current !== game) return;
      }
      const inputSequence = inputSequenceRef.current + 1;
      if (!applyInputToActiveGame(game, queued.control, queued.held, inputSequence)) break;
      inputSequenceRef.current = inputSequence;
    }

    if (runningRef.current && gameRef.current === game && !game.topOut && targetTick > game.tick) {
      processEvents(game.stepTo(targetTick));
    }
  }, [processEvents]);

  const handleInput = useCallback((action: LegacyAction, held: boolean) => {
    if (!runningRef.current || gameRef.current?.topOut) return;
    const control = ACTION_TO_INPUT[action];
    if (requestedInputsRef.current[control] === held) return;
    requestedInputsRef.current[control] = held;
    const game = gameRef.current;
    if (!game) return;
    const applyBeforeTick = quantizeInputTransitionTick(
      game.startTick,
      game.tick,
      control,
      queuedInputsRef.current,
    );
    queuedInputsRef.current.push({
      applyBeforeTick,
      control,
      held,
      sequence: ++inputOrderRef.current,
    });
    queuedInputsRef.current.sort(
      (left, right) => left.applyBeforeTick - right.applyBeforeTick || left.sequence - right.sequence,
    );
  }, []);

  useLegacyKeyboard(running, handleInput, "A", keyBindings);

  useEffect(() => {
    let frame = 0;
    const render = () => {
      const game = gameRef.current;
      if (game && runningRef.current && !game.topOut) {
        const targetTick = Math.floor((performance.now() - originTimeRef.current) * 0.12);
        if (targetTick > game.tick) advanceGame(targetTick);
        const current = gameRef.current;
        if (current === game && runningRef.current) {
          const currentActive = current.active;
          captureAutomaticSnapshot(current.board, currentActive, current.blockIndex);
          setActivePiece(currentActive);
        }
      }
      frame = requestAnimationFrame(render);
    };
    frame = requestAnimationFrame(render);
    return () => cancelAnimationFrame(frame);
  }, [advanceGame, captureAutomaticSnapshot]);

  useEffect(() => {
    if (snapshots.length === 0) {
      setPlaybackFrame(0);
      setPlaybackPlaying(false);
      return;
    }
    setPlaybackFrame((current) => Math.min(current, snapshots.length - 1));
  }, [snapshots.length]);

  useEffect(() => {
    if (!playbackPlaying || snapshots.length < 2) return;
    const timer = window.setInterval(() => {
      setPlaybackFrame((current) => current >= snapshots.length - 1 ? 0 : current + 1);
    }, PLAYBACK_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [playbackPlaying, snapshots.length]);

  const spawnPiece = useCallback((piece: PieceId) => {
    const currentBoard = cloneBoard(boardRef.current);
    const initial: ActivePiece = { piece, rotation: 0, x: 4, y: 1 };
    if (!canPlace(currentBoard, initial)) {
      setMessage("開始位置にブロックがあるため、生成できません。上部の盤面を調整してください。");
      return;
    }

    const seed = newEditorSeed();
    const base = new LegacyGame({ matchId: `board-editor:${Date.now()}`, seed });
    const snapshot = base.snapshot();
    snapshot.board = currentBoard;
    snapshot.active = initial;
    const game = LegacyGame.fromSnapshot(snapshot);

    gameRef.current = game;
    runningRef.current = true;
    originTimeRef.current = performance.now();
    inputSequenceRef.current = 0;
    inputOrderRef.current = 0;
    queuedInputsRef.current = [];
    requestedInputsRef.current = resetRequestedInputs();
    captureAutomaticSnapshot(currentBoard, initial, game.blockIndex);
    setSelectedTool(piece);
    setActivePiece(game.active);
    setRunning(true);
    setPaused(false);
    setMessage(
      `${PIECE_LABELS[piece]}ミノを生成しました。← → 移動 / ↑・Space 右回転 / ろ・Shift 左回転 / ↓ ソフトドロップ。`,
    );
  }, [captureAutomaticSnapshot]);

  function handlePaletteClick(value: BoardTool): void {
    setSelectedTool(value);
    if (value === "garbage-line") {
      if (activePiece) {
        setMessage("落下中はお邪魔ラインを追加できません。操作を終了してから追加してください。");
        return;
      }
      const result = addRandomGarbageRow(boardRef.current);
      setBoard(result.board);
      setMessage(`お邪魔ラインを追加しました。${result.holeColumn + 1}列目を開けています。`);
      return;
    }
    if (value === "garbage-cell") {
      setMessage("お邪魔1個を選択中。盤面の置きたいマスをクリックしてください。");
      return;
    }
    if (isPieceId(value)) {
      spawnPiece(value);
      return;
    }
    setMessage("消去を選択中。盤面をクリックまたはドラッグしてください。");
  }

  function paintCell(x: number, y: number): void {
    if (activePiece) {
      setMessage("落下中は盤面を編集できません。操作を終了してから編集してください。");
      return;
    }
    const cellValue = selectedTool === "garbage-cell"
      ? 8
      : typeof selectedTool === "number" ? selectedTool : null;
    if (cellValue === null) {
      setMessage("お邪魔1列はボタンまたは数字0で追加してください。");
      return;
    }
    setBoard((current) => {
      const next = cloneBoard(current);
      next[y - 1][x - 1] = cellValue;
      return next;
    });
  }

  function stopActivePiece(): void {
    clearActiveGame();
    setMessage("落下を終了しました。盤面を編集できます。");
  }

  function togglePause(): void {
    const game = gameRef.current;
    if (!game || !activePiece) return;
    if (runningRef.current) {
      runningRef.current = false;
      setRunning(false);
      setPaused(true);
      setMessage("落下を一時停止しました。一時保存や再開ができます。");
      return;
    }
    originTimeRef.current = performance.now() - game.tick / 0.12;
    runningRef.current = true;
    setRunning(true);
    setPaused(false);
    setMessage("落下を再開しました。練習画面と同じ速度で動きます。");
  }

  function resetBoard(): void {
    clearActiveGame();
    setBoard(createEmptyBoard());
    setMessage("盤面を空にしました。一時保存したスナップショットは残っています。");
  }

  function saveSnapshot(): void {
    const snapshot: BoardSnapshot = {
      id: `snapshot-${Date.now()}-${++snapshotSequenceRef.current}`,
      board: cloneBoard(boardRef.current),
      active: activePiece ? { ...activePiece } : null,
    };
    if (activePiece && gameRef.current) {
      automaticSnapshotKeyRef.current = activeSnapshotKey(gameRef.current.blockIndex, activePiece);
    }
    setSnapshots((current) => {
      const next = appendRecentSnapshot(current, snapshot);
      setPlaybackFrame(next.length - 1);
      return next;
    });
    setPlaybackPlaying(false);
    setMessage(`盤面を一時保存しました。直近${Math.min(snapshots.length + 1, MAX_SNAPSHOT_FRAMES)}枚を保持します。`);
  }

  function removeSnapshot(id: string): void {
    setPlaybackPlaying(false);
    setSnapshots((current) => current.filter((snapshot) => snapshot.id !== id));
  }

  function clearSnapshots(): void {
    setSnapshots([]);
    setDraggingSnapshotId(null);
    setPlaybackFrame(0);
    setPlaybackPlaying(false);
    automaticSnapshotKeyRef.current = null;
    setMessage("一時保存したスナップショットを全て削除しました。");
  }

  function movePlaybackFrame(delta: number): void {
    if (snapshots.length === 0) return;
    setPlaybackPlaying(false);
    setPlaybackFrame((current) => Math.max(0, Math.min(snapshots.length - 1, current + delta)));
  }

  function togglePlayback(): void {
    if (snapshots.length < 2) return;
    setPlaybackPlaying((current) => !current);
  }

  function pausePlayback(): void {
    setPlaybackPlaying(false);
  }

  function playPlayback(): void {
    if (snapshots.length < 2) return;
    setPlaybackPlaying(true);
  }

  function isTypingTarget(target: EventTarget | null): boolean {
    return target instanceof Element
      && (target.matches("input, textarea, select") || Boolean(target.closest("[contenteditable=\"true\"]")));
  }

  useEffect(() => {
    const handleEditorShortcut = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey || event.repeat || isTypingTarget(event.target)) return;

      if (event.code === "KeyS") {
        event.preventDefault();
        saveSnapshot();
        return;
      }
      if (event.code === "KeyZ") {
        if (snapshots.length === 0) return;
        event.preventDefault();
        movePlaybackFrame(-1);
        return;
      }
      if (event.code === "KeyX") {
        if (snapshots.length < 2) return;
        event.preventDefault();
        togglePlayback();
        return;
      }
      if (event.code === "KeyC") {
        if (snapshots.length === 0) return;
        event.preventDefault();
        movePlaybackFrame(1);
        return;
      }

      const digitMatch = /^(?:Digit|Numpad)([0-9])$/u.exec(event.code);
      if (!digitMatch) return;
      const tool = digitTools[digitMatch[1]];
      if (tool === undefined) return;
      event.preventDefault();
      handlePaletteClick(tool);
    };

    window.addEventListener("keydown", handleEditorShortcut, { passive: false });
    return () => window.removeEventListener("keydown", handleEditorShortcut);
  }, [activePiece, snapshots.length, selectedTool]);

  function reorderSnapshots(sourceId: string, targetId: string): void {
    if (sourceId === targetId) return;
    setSnapshots((current) => {
      const sourceIndex = current.findIndex((snapshot) => snapshot.id === sourceId);
      const targetIndex = current.findIndex((snapshot) => snapshot.id === targetId);
      if (sourceIndex < 0 || targetIndex < 0) return current;
      const next = [...current];
      const [moved] = next.splice(sourceIndex, 1);
      next.splice(targetIndex, 0, moved);
      return next;
    });
  }

  async function downloadSnapshotsAsZip(): Promise<void> {
    if (snapshots.length === 0 || exportingSnapshots) return;
    setExportingSnapshots(true);
    setMessage(`${snapshots.length}枚のPNGをZIPにまとめています…`);
    try {
      const files = await Promise.all(
        snapshots.map(async (snapshot, index) => ({
          name: `bwf-snapshot-${String(index + 1).padStart(3, "0")}.png`,
          data: await boardPngBytes(snapshot.board, snapshot.active),
        })),
      );
      downloadBlob(createStoredZip(files), "bwf-board-snapshots.zip");
      setMessage(`${snapshots.length}枚のスナップショットをZIPでダウンロードしました。`);
    } finally {
      setExportingSnapshots(false);
    }
  }

  function downloadSnapshotsAsGif(): void {
    if (snapshots.length === 0 || exportingSnapshots) return;
    setExportingSnapshots(true);
    setMessage(`${snapshots.length}枚のスナップショットからGIFを作っています…`);
    try {
      downloadBlob(createAnimatedGif(snapshots), "bwf-board-snapshots.gif");
      setMessage(`${snapshots.length}枚のスナップショットをGIFでダウンロードしました。1枚0.5秒です。`);
    } finally {
      setExportingSnapshots(false);
    }
  }

  const currentGame = gameRef.current;
  const elapsedTick = currentGame ? currentGame.tick - currentGame.startTick : 0;
  const gravityTicks = gravityIntervalAtElapsedTick(elapsedTick);
  const gravityLevel = gravityLevelAtElapsedTick(elapsedTick);
  const gravitySeconds = gravityTicks / 120;
  const playbackSnapshot = snapshots[playbackFrame] ?? null;
  const playbackActiveMap = new Map(
    playbackSnapshot?.active
      ? getPoseCells(playbackSnapshot.active).map(({ x, y }) => [`${x}:${y}`, playbackSnapshot.active?.piece ?? 0] as const)
      : [],
  );

  return (
    <main className="game-root board-editor-page">
      <div className="board-editor-shell">
        <header className="mode-header board-editor-header">
          <a className="back-button" href="/">← メニュー</a>
          <span className="mode-header-label">BOARD EDITOR</span>
          <span className="practice-status"><span className="status-dot" /> 10 × 24</span>
        </header>

        <section aria-labelledby="board-editor-title" className="board-editor-panel">
          <p className="brand-kicker">ROTATION GUIDE / BOARD EDITOR</p>
          <h1 className="brand-title" id="board-editor-title">盤面エディタ</h1>
          <p className="board-editor-lead">テトリミノを押すと盤面の上から生成されます。練習画面と同じ操作で動かすと、ポーズが変わるたび自動で一時保存し、着地してライン消去された盤面も最後に追加します。</p>

          <div className="board-editor-layout">
            <section aria-label="編集盤面" className="board-editor-board-column">
              <div className="board-bevel board-editor-bevel">
                <div className="board-well board-editor-well">
                  <div
                    aria-label="10列24行の編集盤面"
                    className="board-grid board-editor-grid"
                    onPointerLeave={() => setPainting(false)}
                    onPointerUp={() => setPainting(false)}
                    role="grid"
                  >
                    {Array.from({ length: BOARD_HEIGHT }, (_, rowIndex) => (
                      Array.from({ length: BOARD_WIDTH }, (_, columnIndex) => {
                        const x = columnIndex + 1;
                        const y = rowIndex + 1;
                        const fixed = board[rowIndex][columnIndex];
                        const activeCell = activeMap.get(`${x}:${y}`);
                        const value = activeCell ? activePiece?.piece ?? fixed : fixed;
                        const conflict = Boolean(activeCell && fixed !== 0);
                        return (
                          <button
                            aria-label={`x${x}, y${y}${fixed === 0 ? " 空き" : " ブロック"}${activeCell ? " 落下中" : ""}`}
                            aria-pressed={fixed !== 0}
                            className={`board-cell board-editor-cell${value ? " tile" : ""}${activeCell ? " active-tile board-editor-active" : ""}${conflict ? " board-editor-conflict" : ""}`}
                            key={`${x}:${y}`}
                            onPointerDown={(event) => {
                              event.preventDefault();
                              setPainting(true);
                              paintCell(x, y);
                            }}
                            onPointerEnter={() => {
                              if (painting) paintCell(x, y);
                            }}
                            onPointerUp={() => setPainting(false)}
                            role="gridcell"
                            style={cellStyle(value)}
                            type="button"
                          />
                        );
                      })
                    ))}
                  </div>
                </div>
              </div>
              <p className="board-editor-coordinates">上が1段目、下が24段目。実際のゲーム盤と同じ10×24マスです。</p>
              {activePiece ? (
                <div className="board-editor-touch-controls">
                  <TouchControls disabled={!running} onInput={handleInput} side="left" />
                </div>
              ) : null}
            </section>

            <aside aria-label="盤面エディタの操作" className="board-editor-controls">
              <div aria-label="保存盤面の再生プレビュー" className="board-editor-playback">
                  <div className="board-editor-playback-header">
                    <div>
                      <h3>再生プレビュー</h3>
                      <p>GIFと同じく1フレーム0.5秒。保存順に繰り返し再生します。</p>
                    </div>
                    <span className="board-editor-playback-status">
                      {snapshots.length > 0 ? `${playbackFrame + 1} / ${snapshots.length}` : "未保存"}
                    </span>
                  </div>
                  {playbackSnapshot ? (
                    <div aria-label={`再生中のスナップショット${playbackFrame + 1}の盤面`} className="board-editor-playback-stage" role="img">
                      {Array.from({ length: BOARD_HEIGHT }, (_, rowIndex) => (
                        Array.from({ length: BOARD_WIDTH }, (_, columnIndex) => {
                          const x = columnIndex + 1;
                          const y = rowIndex + 1;
                          const fixed = playbackSnapshot.board[rowIndex][columnIndex];
                          const moving = playbackActiveMap.get(`${x}:${y}`);
                          const value = moving || fixed;
                          return (
                            <span
                              className={`board-editor-playback-cell${value ? " tile" : ""}${moving ? " active-tile" : ""}`}
                              key={`${x}:${y}`}
                              style={cellStyle(value)}
                            />
                          );
                        })
                      ))}
                    </div>
                  ) : (
                    <p className="board-editor-playback-empty">スナップショットを保存すると、ここで再生できます。</p>
                  )}
                  <div className="board-editor-playback-actions">
                    <button aria-keyshortcuts="Z" disabled={snapshots.length === 0 || playbackFrame === 0} onClick={() => movePlaybackFrame(-1)} type="button">← 戻る</button>
                    <button aria-keyshortcuts="X" disabled={!playbackPlaying} onClick={pausePlayback} type="button">⏸ 一時停止</button>
                    <button aria-keyshortcuts="X" className="board-editor-primary" disabled={playbackPlaying || snapshots.length < 2} onClick={playPlayback} type="button">▶ 再生</button>
                    <button aria-keyshortcuts="C" disabled={snapshots.length === 0 || playbackFrame >= snapshots.length - 1} onClick={() => movePlaybackFrame(1)} type="button">進む →</button>
                    <button disabled={snapshots.length === 0 || playbackFrame === 0} onClick={() => movePlaybackFrame(-PLAYBACK_SKIP_FRAMES)} type="button">⏮ 5戻る</button>
                    <button disabled={snapshots.length === 0 || playbackFrame >= snapshots.length - 1} onClick={() => movePlaybackFrame(PLAYBACK_SKIP_FRAMES)} type="button">5進む ⏭</button>
                  </div>
              </div>

              <div className="board-editor-toolbox">
                <h2>ブロックを選ぶ</h2>
                <p className="board-editor-toolbox-note">I〜Jは押すと生成。お邪魔1列はすぐ追加、消去とお邪魔1個は盤面をクリックします。</p>
                <p className="board-editor-shortcut-note">数字0〜9で選択　Z 戻る / X 一時停止・再生 / C 進む</p>
                <div aria-label="配置または生成するブロック" className="board-editor-palette">
                  {palette.map(({ value, label, description, shortcut }) => (
                    <button
                      aria-label={description}
                      aria-keyshortcuts={shortcut}
                      className={`board-editor-palette-button${selectedTool === value ? " is-selected" : ""}`}
                      key={label}
                      onClick={() => handlePaletteClick(value)}
                      type="button"
                    >
                      <kbd className="board-editor-palette-key">{shortcut}</kbd>
                      <span className={`board-editor-swatch cell-value-${value}`} />
                      {label}
                    </button>
                  ))}
                </div>
                <p className="board-editor-message">{message}</p>
              </div>

              {activePiece ? (
                <div className="board-editor-live-controls" aria-label="落下中の操作">
                  <div>
                    <strong>{PIECE_LABELS[activePiece.piece]}ミノを操作中</strong>
                    <span>
                      {paused ? "一時停止中" : "落下中"} / レベル{gravityLevel} / {gravityTicks} tick（{gravitySeconds.toFixed(2)}秒/段）
                    </span>
                  </div>
                  <div className="board-editor-live-actions">
                    <button className="board-editor-primary" onClick={togglePause} type="button">
                      {running ? "一時停止" : "再開"}
                    </button>
                    <button onClick={stopActivePiece} type="button">操作を終了</button>
                  </div>
                  <p>← → 移動　↑ / Space 右回転　ろ / Shift 左回転　↓ ソフトドロップ</p>
                </div>
              ) : null}

              <div className="board-editor-actions">
                <button aria-keyshortcuts="S" className="board-editor-primary" onClick={saveSnapshot} title="Sキーでも保存できます" type="button">現在の盤面を一時保存</button>
                <button onClick={resetBoard} type="button">盤面を全消去</button>
              </div>

          <section aria-label="一時保存した盤面" className="board-editor-snapshots">
            <div className="board-editor-snapshot-header">
              <div>
                <h2>一時保存した盤面</h2>
                <p>ポーズが変わるたび自動保存し、着地後の盤面を最後に追加します。直近{MAX_SNAPSHOT_FRAMES}枚を保持し、カードはドラッグで並べ替え、GIFは1枚0.5秒、ZIPは各フレームをPNGで収録します。</p>
              </div>
              <span className="board-editor-snapshot-count">{snapshots.length}枚</span>
            </div>

            {snapshots.length > 0 ? (
              <>
                <div className="board-editor-snapshot-actions">
                  <button className="board-editor-primary" disabled={exportingSnapshots} onClick={() => void downloadSnapshotsAsZip()} type="button">
                    ZIPでダウンロード
                  </button>
                  <button disabled={exportingSnapshots} onClick={downloadSnapshotsAsGif} type="button">
                    GIFでダウンロード
                  </button>
                  <button disabled={exportingSnapshots} onClick={clearSnapshots} type="button">
                    一時保存を全消去
                  </button>
                </div>
                <ol className="board-editor-snapshot-list">
                  {snapshots.map((snapshot, index) => {
                    const snapshotActiveMap = new Map(
                      snapshot.active
                        ? getPoseCells(snapshot.active).map(({ x, y }) => [`${x}:${y}`, snapshot.active?.piece ?? 0] as const)
                        : [],
                    );
                    return (
                      <li
                        className={`board-editor-snapshot-card${draggingSnapshotId === snapshot.id ? " is-dragging" : ""}`}
                        draggable
                        key={snapshot.id}
                        onDragEnd={() => setDraggingSnapshotId(null)}
                        onDragOver={(event) => event.preventDefault()}
                        onDragStart={() => setDraggingSnapshotId(snapshot.id)}
                        onDrop={() => {
                          if (draggingSnapshotId) reorderSnapshots(draggingSnapshotId, snapshot.id);
                          setDraggingSnapshotId(null);
                        }}
                      >
                        <div className="board-editor-snapshot-card-header">
                          <strong>#{index + 1}</strong>
                          <button aria-label={`スナップショット${index + 1}を削除`} onClick={() => removeSnapshot(snapshot.id)} type="button">×</button>
                        </div>
                        <div aria-label={`スナップショット${index + 1}の盤面`} className="board-editor-snapshot-thumb" role="img">
                          {Array.from({ length: BOARD_HEIGHT }, (_, rowIndex) => (
                            Array.from({ length: BOARD_WIDTH }, (_, columnIndex) => {
                              const x = columnIndex + 1;
                              const y = rowIndex + 1;
                              const fixed = snapshot.board[rowIndex][columnIndex];
                              const moving = snapshotActiveMap.get(`${x}:${y}`);
                              const value = moving || fixed;
                              return (
                                <span
                                  className={`board-editor-snapshot-cell${value ? " tile" : ""}${moving ? " active-tile" : ""}`}
                                  key={`${x}:${y}`}
                                  style={cellStyle(value)}
                                />
                              );
                            })
                          ))}
                        </div>
                        <span className="board-editor-snapshot-drag-hint">ドラッグで並べ替え</span>
                      </li>
                    );
                  })}
                </ol>
              </>
            ) : (
              <p className="board-editor-snapshot-empty">まだ保存されていません。現在の盤面を一時保存すると、ここに並びます。</p>
            )}
          </section>
            </aside>
          </div>
        </section>
      </div>
    </main>
  );
}
